import React, { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CodeSnippetRenderer from '../../../components/CodeSnippetRenderer';

const MappingStatusChips = ({ required, mappingStatus }) => (
  <>
    {required && <Chip label="Required" size="small" color="warning" />}
    {mappingStatus === 'configured' && <Chip label="Mapped" size="small" color="success" />}
    {mappingStatus === 'unconfigured' && <Chip label="Not mapped" size="small" color="default" />}
  </>
);

const FieldPathHeader = ({ marminPath, required, mappingStatus }) => (
  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
      {marminPath}
    </Typography>
    {mappingStatus !== undefined && <MappingStatusChips required={required} mappingStatus={mappingStatus} />}
  </Stack>
);

const FieldSpecDetail = ({ marminPath, spec, showUnmapped = false, required = false, mappingStatus }) => {
  const [expanded, setExpanded] = useState(false);

  if (!spec || typeof spec !== 'object') {
    if (!showUnmapped) {
      return null;
    }

    return (
      <Box sx={{ py: 1.25 }}>
        <FieldPathHeader marminPath={marminPath} required={required} mappingStatus={mappingStatus ?? 'unconfigured'} />
      </Box>
    );
  }

  const hasPipeline = Array.isArray(spec.pipeline) && spec.pipeline.length > 0;
  const hasConditions = Array.isArray(spec.conditions) && spec.conditions.length > 0;
  const hasDefault = Object.prototype.hasOwnProperty.call(spec, 'default');
  const hasSource = Boolean(spec.source);
  const hasMappingDetails =
    hasSource ||
    hasDefault ||
    hasPipeline ||
    hasConditions ||
    spec.required ||
    (spec.fallback !== undefined && spec.fallback !== '');

  return (
    <Box sx={{ py: 1.25 }}>
      <FieldPathHeader marminPath={marminPath} required={required} mappingStatus={mappingStatus ?? 'configured'} />

      {hasMappingDetails && (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
          {hasSource && (
            <Chip label={`source: ${spec.source}`} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
          )}
          {hasDefault && (
            <Chip
              label={`default: ${JSON.stringify(spec.default)}`}
              size="small"
              variant="outlined"
              sx={{ fontFamily: 'monospace' }}
            />
          )}
          {spec.required && <Chip label="required in mapping" size="small" color="warning" variant="outlined" />}
          {spec.fallback !== undefined && spec.fallback !== '' && (
            <Chip label={`fallback: ${String(spec.fallback)}`} size="small" variant="outlined" />
          )}
          {hasPipeline && <Chip label={`${spec.pipeline.length} pipeline step(s)`} size="small" variant="outlined" />}
          {hasConditions && <Chip label={`${spec.conditions.length} condition(s)`} size="small" variant="outlined" />}
        </Stack>
      )}

      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
        disableGutters
        elevation={0}
        sx={{
          mt: hasMappingDetails ? 0.75 : 0.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          backgroundColor: 'action.hover',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon fontSize="small" />}
          sx={{
            minHeight: 32,
            px: 1.25,
            py: 0,
            '& .MuiAccordionSummary-content': { my: 0.5 },
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Full field spec (JSON)
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 1.25, pt: 0, pb: 1.25 }}>
          <CodeSnippetRenderer title="" payload={spec} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default FieldSpecDetail;
